import React from "react";
import cn from "clsx";
import { DesignSize, DesignTheme } from "../../types";
import { NOOP, isString } from "../../utils/helpers";
import Icon from "../Icon";
import "./Button.css";

export type ButtonType = "button" | "submit" | "reset";
export type ButtonSize = DesignSize;
export type ButtonShape = "default" | "solid" | "outline" | "link" | "none";
export type ButtonTheme = DesignTheme;

export interface ButtonProps {
  /** The type of button (note: the type will affect form submit). */
  type?: ButtonType;
  /** The name of an icon, or an element to use. */
  icon?: string | React.ReactNode;
  /**
   * A label to give to the button.
   *
   * @default ""
   */
  label?: string;
  /**
   * The size keyword affects the button's CSS (`button-size-{size}`).
   *
   * @default "m"
   */
  size?: ButtonSize;
  /**
   * The shape keyword affects the button's CSS (`button-shape-{shape}`).
   *
   * @default "default"
   */
  shape?: ButtonShape;
  /**
   * The theme keyword affects the button's CSS (`button-theme-{theme}`).
   *
   * @default "neutral"
   */
  theme?: ButtonTheme;
  /** Boolean to modify the button's width. */
  fullWidth?: boolean;
  /** Boolean to control if the button is disabled or not. */
  disabled?: boolean;
  /** Extra class names to give to the button. */
  className?: string;
  /**
   * A specific `aria-label` to give the button. If the button already has a
   * label value then this is not necessary.
   *
   * If you are implementing a button with no visual label, then please add an
   * aria-label to ensure accessibility.
   *
   * If you have set the icon prop to a string (i.e. using a pre-defined icon)
   * then the aria-label will use the icon's name (if not explicitly set).
   */
  ariaLabel?: string;
  /** Any elements to display within the button. */
  children?: React.ReactNode | React.ReactNodeArray;
  /** Event handler for when the button is clicked. */
  onClick?: React.MouseEventHandler;
}

export function Button({
  type = "button",
  icon,
  label,
  shape = "default",
  theme = "neutral",
  size = "m",
  fullWidth,
  disabled,
  className,
  ariaLabel,
  children,
  onClick
}: ButtonProps) {
  const classNames = cn(
    "button",
    className,
    !!size && `button-size-${size}`,
    !!theme && `button-theme-${theme}`,
    !!shape && `button-shape-${shape}`,
    !!icon && `has-icon`,
    !!fullWidth && `is-fullwidth`,
    !!disabled && "is-disabled"
  );

  return (
    <button
      type={type}
      disabled={disabled}
      className={classNames}
      aria-label={ariaLabel || label || (isString(icon) ? String(icon) : "")}
      onClick={!disabled && onClick ? onClick : NOOP}
    >
      {!!icon && isString(icon) && <Icon name={String(icon)} />}
      {!!icon && !isString(icon) && <Icon>{icon}</Icon>}
      {!children && !!label && <div className="button-label">{label}</div>}
      {children}
    </button>
  );
}

export default Button;
